import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { apipayCheckout } from "../../../redux/actions/payment/apipayCheckout";
import { apipayOrderStatus } from "../../../redux/actions/payment/apipayOrderStatus";
import { apipayGetPhones } from "../../../redux/actions/payment/apipayGetPhones";
import {
  resetApipayCheckout,
  resetApipayOrderStatus,
} from "../../../redux/slices/payment.slice";

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_DURATION_MS = 3 * 60 * 1000; // 3 минуты
const TERMINAL_STATUSES = ["paid", "cancelled", "expired", "refunded"];

// ───────────────────────────── Step indicator ─────────────────────────────

const StepIndicator = ({ currentStep }) => {
  const steps = [
    { label: "Номер телефона" },
    { label: "Ожидание оплаты" },
    { label: "Результат" },
  ];

  return (
    <div className="tw-flex tw-items-center tw-justify-center tw-mb-10">
      {steps.map((step, idx) => {
        const stepNum = idx + 1;
        const isActive = stepNum === currentStep;
        const isDone = stepNum < currentStep;

        return (
          <React.Fragment key={idx}>
            {/* Circle */}
            <div className="tw-flex tw-flex-col tw-items-center tw-relative">
              <div
                className={`tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-font-bold tw-text-sm tw-transition-all tw-duration-300 tw-border-2 ${
                  isDone
                    ? "tw-bg-blue-600 tw-border-blue-600 tw-text-white"
                    : isActive
                    ? "tw-bg-white tw-border-blue-600 tw-text-blue-600 tw-shadow-lg tw-shadow-blue-200"
                    : "tw-bg-white tw-border-gray-300 tw-text-gray-400"
                }`}
              >
                {isDone ? (
                  <svg
                    className="tw-w-5 tw-h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  stepNum
                )}
              </div>
              <span
                className={`tw-absolute tw-top-12 tw-text-xs tw-font-medium tw-whitespace-nowrap ${
                  isActive || isDone ? "tw-text-blue-600" : "tw-text-gray-400"
                }`}
              >
                {step.label}
              </span>
            </div>

            {/* Connecting line */}
            {idx < steps.length - 1 && (
              <div
                className={`tw-flex-1 tw-h-0.5 tw-mx-3 tw-transition-all tw-duration-300 tw-max-w-[120px] ${
                  stepNum < currentStep ? "tw-bg-blue-600" : "tw-bg-gray-300"
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ───────────────────────────── Step 1: Phone ─────────────────────────────

const StepPhone = ({
  phoneNumber,
  setPhoneNumber,
  savedPhones,
  phonesLoading,
  onSubmit,
  checkoutLoading,
  orderInfo,
}) => {
  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length === 0) return "";

    let formatted = "";
    const d = digits.startsWith("7") ? digits : "7" + digits;

    if (d.length > 0) formatted += "+" + d.substring(0, 1);
    if (d.length > 1) formatted += " (" + d.substring(1, 4);
    if (d.length > 4) formatted += ") " + d.substring(4, 7);
    if (d.length > 7) formatted += "-" + d.substring(7, 9);
    if (d.length > 9) formatted += "-" + d.substring(9, 11);

    return formatted;
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (raw.length <= 11) {
      setPhoneNumber(raw);
    }
  };

  const selectSavedPhone = (phone) => {
    const digits = phone.replace(/\D/g, "");
    setPhoneNumber(digits);
  };

  const isPhoneValid = phoneNumber.replace(/\D/g, "").length === 11;

  return (
    <div className="tw-max-w-lg tw-mx-auto">
      {/* Order summary card */}
      <div className="tw-bg-gradient-to-r tw-from-blue-50 tw-to-indigo-50 tw-rounded-2xl tw-p-6 tw-border tw-border-blue-200 tw-mb-8">
        <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-3">
          Ваш заказ
        </h3>
        <div className="tw-flex tw-justify-between tw-items-center">
          <div>
            <p className="tw-text-gray-700 tw-font-medium">{orderInfo?.name}</p>
          
          </div>
          <div className="tw-text-right">
            <p className="tw-text-2xl tw-font-bold tw-text-blue-600">
              {Number(orderInfo?.finalPrice).toLocaleString("ru-RU")} ₸
            </p>
            {orderInfo?.savings > 0 && (
              <p className="tw-text-xs tw-text-green-600 tw-font-medium">
                Экономия{" "}
                <span className="tw-whitespace-nowrap">
                  {Number(orderInfo?.savings).toLocaleString("ru-RU")} ₸
                </span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Phone input */}
      <div className="tw-mb-6">
        <label className="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2">
          Введите номер{" "}
          <img
            src="https://kaspi.kz/img/Logo.svg"
            alt="Kaspi"
            className="tw-h-5 tw-w-auto tw-inline-block tw-align-middle tw-mx-1"
            onError={(e) => {
              // Fallback to a simple red circle if logo fails to load
              e.target.style.display = "none";
            }}
          />{" "}
          для получения счёта
        </label>
        <input
          type="tel"
          value={formatPhone(phoneNumber)}
          onChange={handlePhoneChange}
          placeholder="+7 (___) ___-__-__"
          className="tw-w-full tw-px-4 tw-py-4 tw-border-2 tw-border-gray-300 tw-rounded-xl tw-text-gray-900 tw-text-lg tw-font-medium tw-transition-all tw-duration-200 focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-4 focus:tw-ring-blue-100 placeholder:tw-text-gray-400"
          autoFocus
        />
      </div>

      {/* Saved phones */}
      {!phonesLoading && savedPhones && savedPhones.length > 0 && (
        <div className="tw-mb-8">
          <p className="tw-text-sm tw-font-medium tw-text-gray-600 tw-mb-2">
            Ранее использованные номера:
          </p>
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {savedPhones.map((p) => (
              <button
                key={p.id}
                onClick={() => selectSavedPhone(p.phone_number)}
                className={`tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-transition-all tw-duration-200 tw-border ${
                  phoneNumber.replace(/\D/g, "") ===
                  p.phone_number.replace(/\D/g, "")
                    ? "tw-bg-blue-600 tw-text-white tw-border-blue-600"
                    : "tw-bg-white tw-text-gray-700 tw-border-gray-300 hover:tw-border-blue-400 hover:tw-text-blue-600"
                }`}
              >
                {formatPhone(p.phone_number)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Submit */}
      <button
        onClick={onSubmit}
        disabled={!isPhoneValid || checkoutLoading}
        className="tw-w-full tw-bg-gradient-to-r tw-from-blue-600 tw-to-blue-700 tw-text-white tw-font-semibold tw-py-4 tw-px-6 tw-rounded-xl tw-transition-all tw-duration-200 hover:tw-from-blue-700 hover:tw-to-blue-800 hover:tw-shadow-lg hover:tw-transform hover:tw-scale-[1.01] focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-blue-500 focus:tw-ring-opacity-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed disabled:hover:tw-transform-none"
      >
        {checkoutLoading ? (
          <span className="tw-flex tw-items-center tw-justify-center tw-gap-2">
            <svg
              className="tw-animate-spin tw-h-5 tw-w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="tw-opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="tw-opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Создание счёта...
          </span>
        ) : (
          "Отправить счёт"
        )}
      </button>
    </div>
  );
};

// ───────────────────────────── Step 2: Pending ─────────────────────────────

const StepPending = ({ orderData, onExpired }) => {
  const [secondsLeft, setSecondsLeft] = useState(3 * 60); // 3 minutes

  useEffect(() => {
    if (secondsLeft <= 0) return;

    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [secondsLeft <= 0]); // only re-run if we cross the zero boundary

  // When timer expires, notify parent to move to result step
  useEffect(() => {
    if (secondsLeft === 0 && onExpired) {
      onExpired();
    }
  }, [secondsLeft, onExpired]);

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const timerDisplay = `${minutes}:${seconds.toString().padStart(2, "0")}`;
  const isExpired = secondsLeft <= 0;
  const progressPercent = (secondsLeft / (3 * 60)) * 100;

  return (
    <div className="tw-max-w-lg tw-mx-auto tw-text-center">


      <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-3">
        Ожидание оплаты
      </h2>

      {/* Countdown timer */}
      <div className="tw-mb-6">
        <div
          className={`tw-text-4xl tw-font-mono tw-font-bold tw-mb-2 ${
            isExpired
              ? "tw-text-red-600"
              : secondsLeft <= 30
              ? "tw-text-red-500"
              : secondsLeft <= 60
              ? "tw-text-amber-500"
              : "tw-text-blue-600"
          }`}
        >
          {isExpired ? "Время истекло" : timerDisplay}
        </div>
        {/* Progress bar */}
        <div className="tw-w-48 tw-h-1.5 tw-bg-gray-200 tw-rounded-full tw-mx-auto tw-overflow-hidden">
          <div
            className={`tw-h-full tw-rounded-full tw-transition-all tw-duration-1000 tw-ease-linear ${
              isExpired
                ? "tw-bg-red-500"
                : secondsLeft <= 30
                ? "tw-bg-red-500"
                : secondsLeft <= 60
                ? "tw-bg-amber-500"
                : "tw-bg-blue-600"
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <p className="tw-text-xs tw-text-gray-400 tw-mt-2">
          {isExpired
            ? "Время на оплату истекло"
            : "Оставшееся время для оплаты"}
        </p>
      </div>

      <p className="tw-text-gray-600 tw-mb-8 tw-leading-relaxed">
        Счёт отправлен на ваш номер. Откройте приложение Kaspi и подтвердите
        оплату. Эта страница обновится автоматически.
      </p>

      {/* Order details card */}
      <div className="tw-bg-gray-50 tw-rounded-2xl tw-p-6 tw-border tw-border-gray-200 tw-text-left">
        <div className="tw-space-y-3">
          <div className="tw-flex tw-justify-between">
            <span className="tw-text-gray-500 tw-text-sm">Номер заказа</span>
            <span className="tw-font-semibold tw-text-gray-900">
              #{orderData?.order_id}
            </span>
          </div>
          <div className="tw-flex tw-justify-between">
            <span className="tw-text-gray-500 tw-text-sm">Сумма</span>
            <span className="tw-font-semibold tw-text-gray-900">
              {Number(orderData?.amount).toLocaleString("ru-RU")} ₸
            </span>
          </div>
          <div className="tw-flex tw-justify-between">
            <span className="tw-text-gray-500 tw-text-sm">Статус</span>
            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-amber-600 tw-font-medium tw-text-sm">
              <span className="tw-w-2 tw-h-2 tw-bg-amber-500 tw-rounded-full tw-animate-pulse" />
              Ожидает оплаты
            </span>
          </div>
        </div>
      </div>

      <p className="tw-text-xs tw-text-gray-400 tw-mt-6">
        Проверка статуса происходит автоматически каждые 3 секунды
      </p>
    </div>
  );
};

// ───────────────────────────── Step 3: Result ─────────────────────────────

const StepResult = ({ orderStatusData, onGoBack, timerExpired }) => {
  const isPaid = orderStatusData?.status === "paid";
  const isCancelled = orderStatusData?.status === "cancelled";
  const isExpired = timerExpired || orderStatusData?.status === "expired";

  return (
    <div className="tw-max-w-lg tw-mx-auto tw-text-center">
      {/* Status icon */}
      <div className="tw-mb-6">
        {isPaid ? (
          <div className="tw-w-20 tw-h-20 tw-mx-auto tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
            <svg
              className="tw-w-10 tw-h-10 tw-text-green-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        ) : (
          <div className="tw-w-20 tw-h-20 tw-mx-auto tw-bg-red-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
            <svg
              className="tw-w-10 tw-h-10 tw-text-red-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}
      </div>

      <h2
        className={`tw-text-2xl tw-font-bold tw-mb-3 ${
          isPaid ? "tw-text-green-700" : "tw-text-red-700"
        }`}
      >
        {isPaid
          ? "Оплата прошла успешно!"
          : isCancelled
          ? "Оплата отменена"
          : isExpired
          ? "Время оплаты истекло"
          : "Оплата не удалась"}
      </h2>

      <p className="tw-text-gray-600 tw-mb-8">
        {isPaid
          ? "Спасибо за оплату! Ваш тариф уже активирован."
          : isCancelled
          ? "Вы отменили оплату. Попробуйте снова."
          : isExpired
          ? "Срок действия счёта истёк. Попробуйте создать новый."
          : "Что-то пошло не так. Попробуйте ещё раз."}
      </p>

      {/* Order details */}
      <div className="tw-bg-gray-50 tw-rounded-2xl tw-p-6 tw-border tw-border-gray-200 tw-text-left tw-mb-8">
        <div className="tw-space-y-3">
          <div className="tw-flex tw-justify-between">
            <span className="tw-text-gray-500 tw-text-sm">Номер заказа</span>
            <span className="tw-font-semibold tw-text-gray-900">
              #{orderStatusData?.order_id}
            </span>
          </div>
          {orderStatusData?.catalog_item?.name && (
            <div className="tw-flex tw-justify-between">
              <span className="tw-text-gray-500 tw-text-sm">Тариф</span>
              <span className="tw-font-semibold tw-text-gray-900">
                {orderStatusData.catalog_item.name}
              </span>
            </div>
          )}
          <div className="tw-flex tw-justify-between">
            <span className="tw-text-gray-500 tw-text-sm">Сумма</span>
            <span className="tw-font-semibold tw-text-gray-900">
              {Number(orderStatusData?.amount).toLocaleString("ru-RU")} ₸
            </span>
          </div>
          <div className="tw-flex tw-justify-between">
            <span className="tw-text-gray-500 tw-text-sm">Статус</span>
            <span
              className={`tw-font-semibold tw-text-sm ${
                isPaid ? "tw-text-green-600" : "tw-text-red-600"
              }`}
            >
              {isPaid
                ? "Оплачено"
                : isCancelled
                ? "Отменено"
                : isExpired
                ? "Истекло"
                : orderStatusData?.status}
            </span>
          </div>
          {orderStatusData?.paid_at && (
            <div className="tw-flex tw-justify-between">
              <span className="tw-text-gray-500 tw-text-sm">
                Дата оплаты
              </span>
              <span className="tw-font-semibold tw-text-gray-900 tw-text-sm">
                {new Date(orderStatusData.paid_at).toLocaleString("ru-RU")}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="tw-flex tw-flex-col tw-gap-3">
        {isPaid ? (
          <button
            onClick={() => onGoBack("/overview")}
            className="tw-w-full tw-bg-gradient-to-r tw-from-green-600 tw-to-green-700 tw-text-white tw-font-semibold tw-py-4 tw-px-6 tw-rounded-xl tw-transition-all tw-duration-200 hover:tw-from-green-700 hover:tw-to-green-800 hover:tw-shadow-lg focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-green-500 focus:tw-ring-opacity-50"
          >
            Перейти в кабинет
          </button>
        ) : (
          <>
            <button
              onClick={() => onGoBack("/pricing")}
              className="tw-w-full tw-bg-gradient-to-r tw-from-blue-600 tw-to-blue-700 tw-text-white tw-font-semibold tw-py-4 tw-px-6 tw-rounded-xl tw-transition-all tw-duration-200 hover:tw-from-blue-700 hover:tw-to-blue-800 hover:tw-shadow-lg focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-blue-500 focus:tw-ring-opacity-50"
            >
              Попробовать снова
            </button>
          </>
        )}
        <button
          onClick={() => onGoBack("/pricing")}
          className="tw-w-full tw-text-gray-600 tw-font-medium tw-py-3 tw-px-6 tw-rounded-xl tw-transition-colors tw-duration-200 hover:tw-text-gray-900 hover:tw-bg-gray-100"
        >
          Вернуться к тарифам
        </button>
      </div>
    </div>
  );
};

// ───────────────────────────── Main CheckoutPage ─────────────────────────────

const CheckoutPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const orderInfo = location.state; // passed from NewTariffDetail

  const {
    apipayCheckout: { data: checkoutData, loading: checkoutLoading, error: checkoutError },
    apipayOrderStatus: { data: orderStatusData },
    apipayPhones: { data: savedPhones, loading: phonesLoading },
  } = useSelector((state) => state.payment);

  const [step, setStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [timerExpired, setTimerExpired] = useState(false);
  const pollRef = useRef(null);

  // Redirect if no order info
  useEffect(() => {
    if (!orderInfo?.tariff_id) {
      navigate("/pricing", { replace: true });
    }
  }, [orderInfo, navigate]);

  // Load saved phones on mount
  useEffect(() => {
    dispatch(apipayGetPhones());
    return () => {
      dispatch(resetApipayCheckout());
      dispatch(resetApipayOrderStatus());
    };
  }, [dispatch]);

  // Pre-fill phone from saved phones
  useEffect(() => {
    if (savedPhones && savedPhones.length > 0 && !phoneNumber) {
      const digits = savedPhones[0].phone_number.replace(/\D/g, "");
      setPhoneNumber(digits);
    }
  }, [savedPhones, phoneNumber]);

  // Handle checkout success → move to step 2
  useEffect(() => {
    if (checkoutData?.order_id && step === 1) {
      setStep(2);
    }
  }, [checkoutData, step]);

  // Handle checkout error
  useEffect(() => {
    if (checkoutError) {
      const msg = checkoutError?.error || "Ошибка при создании заказа";
      toast.error(msg, { position: "top-center", autoClose: 5000 });
    }
  }, [checkoutError]);

  // Poll for status when on step 2
  const pollStatus = useCallback(() => {
    if (checkoutData?.order_id) {
      dispatch(apipayOrderStatus(checkoutData.order_id));
    }
  }, [dispatch, checkoutData]);

  useEffect(() => {
    if (step === 2 && checkoutData?.order_id) {
      // Initial poll immediately
      pollStatus();

      const startTime = Date.now();

      pollRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;

        if (elapsed >= MAX_POLL_DURATION_MS) {
          clearInterval(pollRef.current);
          pollRef.current = null;

          toast.warning(
            "Проверка статуса платежа остановлена после 3 минут. Если оплата прошла, обновите страницу или проверьте тарифы.",
            { position: "top-center", autoClose: 7000 }
          );

          return;
        }

        pollStatus();
      }, POLL_INTERVAL_MS);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [step, checkoutData, pollStatus]);

  // Check if payment reached terminal status → step 3
  useEffect(() => {
    if (
      step === 2 &&
      orderStatusData?.status &&
      TERMINAL_STATUSES.includes(orderStatusData.status)
    ) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      setStep(3);
    }
  }, [orderStatusData, step]);

  // Handle timer expiration from StepPending
  const handleTimerExpired = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    // Move to step 3 with expired status
    setTimerExpired(true);
    setStep(3);
  }, []);

  // ─── Handlers ───

  const handleSubmitPhone = () => {
    const digits = phoneNumber.replace(/\D/g, "");
    if (digits.length !== 11) {
      toast.error("Введите корректный номер телефона", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    const formatted = `+${digits}`;

    dispatch(
      apipayCheckout({
        tariff_id: orderInfo.tariff_id,
        phone_number: formatted,
        promo_code: orderInfo.promoCode || "",
      })
    );
  };

  const handleGoBack = (path) => {
    navigate(path);
  };

  // ─── Render ───

  if (!orderInfo?.tariff_id) return null;

  return (
    <>
      <Helmet>
        <title>Оплата</title>
      </Helmet>

      <div className="tw-min-h-[calc(100vh-160px)] tw-bg-gradient-to-br tw-from-slate-50 tw-to-blue-50 tw-py-10 tw-px-4">
        <div className="tw-max-w-2xl tw-mx-auto">
          {/* Header */}
          <div className="tw-text-center tw-mb-6">
            <h1 className="tw-text-3xl tw-font-bold tw-text-gray-900">
              Оформление оплаты
            </h1>
          </div>

          {/* Step indicator */}
          <StepIndicator currentStep={step} />

          {/* Step content */}
          <div className="tw-mt-16 tw-bg-white tw-rounded-2xl tw-shadow-xl tw-p-8 tw-border tw-border-gray-200">
            {step === 1 && (
              <StepPhone
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
                savedPhones={savedPhones}
                phonesLoading={phonesLoading}
                onSubmit={handleSubmitPhone}
                checkoutLoading={checkoutLoading}
                orderInfo={orderInfo}
              />
            )}

            {step === 2 && <StepPending orderData={checkoutData} onExpired={handleTimerExpired} />}

            {step === 3 && (
              <StepResult
                orderStatusData={orderStatusData}
                onGoBack={handleGoBack}
                timerExpired={timerExpired}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutPage;

